<template>
  <div>
    <my-header></my-header>

    <div class="rushes-box">
      <div class="h-panel">
        <b-button v-b-modal.help-modal>Help</b-button>

        <b-button
          v-if="mayManageRush"
          :to="{name: 'new_rush'}"
          variant="primary"
        >
          New rush
        </b-button>

        <b-button
          :disabled="isDeleteRushesButtonDisabled"
          :variant="deleteRushesButtonVariant"
          @click="deleteRushesWithConfirmation"
        >
          Delete
        </b-button>
      </div>

      <h2>Rushes</h2>
      <table class="mb-2">
        <tr>
          <th class="pr-1">
            <label for="status-selector" class="mb-0">Status</label>
          </th>
          <td class="pr-4">
            <b-form-select
              id="status-selector"
              v-model="status"
              :options="statusOptions"
            />
          </td>

          <th class="pr-1">
            <label for="window-past-duration-selector" class="mb-0">Past</label>
          </th>
          <td class="pr-4">
            <b-form-select
              id="window-past-duration-selector"
              v-model="windowPastDuration"
              :options="windowDurationOptions"
            />
          </td>

          <th class="pr-1">
            <label for="window-future-duration-selector" class="mb-0">Future</label>
          </th>
          <td>
            <b-form-select
              id="window-future-duration-selector"
              v-model="windowFutureDuration"
              :options="windowDurationOptions"
            />
          </td>
        </tr>
      </table>

      <b-overlay v-if="isRushTableVisible" :show="isRushTableOverlayVisible" :opacity="0.6" style="width: max-content" no-center>
        <template #overlay>
          <div class="d-flex justify-content-center mt-5">
            <b-spinner/>
          </div>
        </template>
        <rush-table
          :containers="rushContainers"
          :nowSec="nowSec"
          :mayManageRush="mayManageRush"
          v-on:rushes-selection-requested="selectRushes"
          v-on:rushes-deselection-requested="deselectRushes"
          v-on:rush-finalization-requested="finishRushWithConfirmation"
          v-on:rush-deletion-requested="deleteRushWithConfirmation"
        />
      </b-overlay>

      <loading-indicator v-if="isLoading && !isRushTableVisible"/>

      <p v-if="message !== null" class="mt-3">
        {{message}}
      </p>
    </div>

    <b-modal id="help-modal" title="Справка" ok-only ok-title="Закрыть">
      <div class="help">
        <p>
          На данной странице показаны все ражи.
        </p>
        <p>
          Раж — соревнование между игроками (а не между клубами).
        </p>
      </div>
    </b-modal>

  </div>
</template>

<script>
/**
 * @typedef { import('../auth.js').Actor } Actor
 */
import auth from '../auth.js';

import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

/**
 * @typedef { import('../model/rush.js').RushPreview } RushPreview
 * @typedef { import('../model/rush.js').RushPreviewContainer } RushPreviewContainer
 */

import { mayManageRush         } from '../model/rush.js';
import { RUSH_STATUS__FINISHED } from '../model/rush.js';
import { getRushStatusOptionsForFilter } from '../model/rush.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { TIME_UNIT_MONTH          } from '../model/event_time_filter.js';
import { getWindowDurationOptions } from '../model/event_time_filter.js';
import { getBegin                 } from '../model/event_time_filter.js';
import { getEnd                   } from '../model/event_time_filter.js';

import { convertMomentToStamp } from '../util.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import RushTable from './RushTable.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'Rushes',

  components: {
    MyHeader,
    LoadingIndicator,
    RushTable,
  },

  data() {
    const DURATION_1_MONTH = {timeUnit: TIME_UNIT_MONTH, amount: 1};

    return {
      /** @type {?Actor} */
      actor: auth.getViewer(),

      loadingStatus: LOADING_STATUS__INIT,

      totalRushAmount: null,

      /** @type {RushPreviewContainer[] | null} */
      rushContainers: null,

      //-----------------------------------------------------------------------
      // filter
      //-----------------------------------------------------------------------

      statusOptions: getRushStatusOptionsForFilter(),
      status: null,

      windowDurationOptions: getWindowDurationOptions(),
      windowPastDuration:   DURATION_1_MONTH,
      windowFutureDuration: DURATION_1_MONTH,

      //-----------------------------------------------------------------------

      nowSec: Date.now() / 1000,
    };
  },

  computed: {
    /** @returns {boolean} */
    mayManageRush() {
      return mayManageRush(this.actor.role);
    },

    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal) {
        const containers = this.rushContainers;
        if(containers !== null && containers.length === 0) {
          return this.emptyListMessage;
        }
      }

      return null;
    },

    emptyListMessage() {
      const totalRushAmount = this.totalRushAmount;
      if(totalRushAmount === null) {
        return 'error';
      }

      if(totalRushAmount === 0) {
        return 'No rushes.';
      }

      // Нет ражей, которые соответствуют указанным условиям.
      return 'No rushes that match the specified conditions.';
    },

    isRushTableVisible() {
      const containers = this.rushContainers;
      if(containers === null) {
        return false;
      }

      return containers.length > 0;
    },

    isRushTableOverlayVisible() {
      const ready = this.isLoadedNormal && this.isRushTableVisible;
      return !ready;
    },

    //-------------------------------------------------------------------------
    // Deletion
    //-------------------------------------------------------------------------

    /** @returns {string} */
    deleteRushesButtonVariant() {
      if(this.hasSelectedRushes) {
        return 'danger';
      } else {
        return 'light';
      }
    },

    /** @returns {boolean} */
    isDeleteRushesButtonDisabled() {
      return !this.hasSelectedRushes;
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    /** @returns {boolean} */
    hasSelectedRushes() {
      const containers = this.rushContainers;
      if(containers === null) {
        return false;
      }

      for(const container of containers) {
        if(container.selected) {
          return true;
        }
      }

      return false;
    },

    /** @returns {number[] | null} */
    selectedRushIds() {
      const containers = this.rushContainers;
      if(containers === null) {
        return null;
      }

      const ids = [];
      for(const container of containers) {
        if(container.selected) {
          ids.push(container.rush.id);
        }
      }

      return ids;
    },

    //-------------------------------------------------------------------------
  },

  created() {
    this.loadRushes();
  },

  watch: {
    status() {
      this.loadRushes();
    },

    windowPastDuration() {
      this.loadRushes();
    },

    windowFutureDuration() {
      this.loadRushes();
    },
  },

  methods: {
    loadRushes() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const requestConf = {
        params: {}
      };

      const status = this.status;
      if(status !== null) {
        requestConf.params.status = status;
      }

      const now = convertMomentToStamp(new Date());

      const windowPastDuration = this.windowPastDuration;
      if(windowPastDuration !== null) {
        requestConf.params.begin = getBegin(now, windowPastDuration);
      }

      const windowFutureDuration = this.windowFutureDuration;
      if(windowFutureDuration !== null) {
        requestConf.params.end = getEnd(now, windowFutureDuration);
      }

      const url = '/rushes';

      HTTP.get(url, requestConf)
      .then(response => {
        /** @type {RushPreview[] | null} */
        const data = response.data.data;
        
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.totalRushAmount = data.totalRushAmount;

        const rushes = data.rushes;
        this.rushContainers = convertRushPreviewsToRushPreviewContainers(rushes);

        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    selectRushes() {
      for(const container of this.rushContainers) {
        if(!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectRushes() {
      for(const container of this.rushContainers) {
        if(container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------
    // Finish
    //-------------------------------------------------------------------------

    finishRushWithConfirmation(rush) {
      notifier.deleteSenderNotifications(this);

      this.confirmRushFinish(
        rush.id,
        () => {
          this.finishRush(rush);
        },
      );
    },

    /**
     * @param {number} rushId
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRushFinish(rushId, onConfirmed) {
      const messageTemplate = `Are you sure you want to finish rush ${rushId} {env}?`;

      confirmAction(
        this,
        messageTemplate,
        'Finish',
        onConfirmed,
      );
    },

    finishRush(rush) {
      const url = `/rush/${rush.id}/finalize`;

      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        rush.frtime = data.frtime;
        rush.status = RUSH_STATUS__FINISHED;
        notifier.showSuccess('Rush finished.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Deletion (1 rush)
    //-------------------------------------------------------------------------

    /** @param {number} rushId */
    deleteRushWithConfirmation(rushId) {
      notifier.deleteSenderNotifications(this);

      this.confirmRushDeletion(
        rushId,
        () => {
          this.deleteRush(rushId);
        }
      );
    },

    /**
     * @param {number} rushId
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRushDeletion(rushId, onConfirmed) {
      const messageTemplate = getRushDeletionConfirmationMessage(rushId);

      confirmAction(
        this,
        messageTemplate,
        'Delete',
        onConfirmed,
      );
    },

    /** @param {number} rushId */
    deleteRush(rushId) {
      const url = `/rush/${rushId}/delete`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.totalRushAmount--;
        deleteRushContainerFromArray(this.rushContainers, rushId);
        notifier.showSuccess('Rush deleted.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Deletion (many rushes)
    //-------------------------------------------------------------------------

    deleteRushesWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const rushIds = this.selectedRushIds;

      this.confirmRushesDeletion(
        rushIds,
        () => {
          this.deleteRushes(rushIds);
        },
      );
    },

    /**
     * @param {number[]} rushIds
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRushesDeletion(rushIds, onConfirmed) {
      const params = {};
      if(rushIds.length > 1) {
        params.description = getRushIdsInfo(rushIds);
      }

      confirmAction(
        this,
        getRushesDeletionConfirmationMessage(rushIds),
        'Delete',
        onConfirmed,
        params,
      );
    },

    /** @param {number[]} rushIds */
    deleteRushes(rushIds) {
      const idsStr = rushIds.join(',');

      const data = new FormData();
      data.set('ids', idsStr);

      HTTP.post('/rushes/delete', data)
      .then(() => {
        deleteRushContainersFromArray(this.rushContainers, rushIds);
        this.totalRushAmount -= rushIds.length;

        const message = getRushesDeletionCompleteMessage(rushIds.length);
        notifier.showSuccess(message);
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
  },
};

/**
 * @param {RushPreview[]} rushes
 * @returns {RushPreviewContainer[]}
 */
function convertRushPreviewsToRushPreviewContainers(rushes) {
  const containers = [];

  for(const rush of rushes) {
    const container = {
      rush: rush,
      selected: false,
    };
    containers.push(container);
  }

  return containers;
}

//-----------------------------------------------------------------------------
// Deletion (1 rush)
//-----------------------------------------------------------------------------

/** @param {number} id */
function getRushDeletionConfirmationMessage(id) {
  return `Are you sure you want to delete rush ${id} {env}?`
}

/**
 * @param {RushPreviewContainer[]} containers 
 * @param {number} rushId
 */
function deleteRushContainerFromArray(containers, rushId) {
  const index = getRushContainerIndex(containers, rushId);
  if(index !== null) {
    containers.splice(index, 1);
  }
}

/**
 * @param {RushPreviewContainer[]} containers
 * @param {number} rushId
 * @returns {number | null}
 */
function getRushContainerIndex(containers, rushId) {
  for(let i = 0; i < containers.length; i++) {
    const container = containers[i];
    if(container.rush.id === rushId) {
      return i;
    }
  }

  return null;
}

//-----------------------------------------------------------------------------
// Deletion (many rushes)
//-----------------------------------------------------------------------------

function getRushesDeletionConfirmationMessage(ids) {
  if(ids.length === 1) {
    const id = ids[0];
    return getRushDeletionConfirmationMessage(id);
  }

  const amount = ids.length;
  return `Are you sure you want to delete ${amount} rushes {env}?`;
}

function getRushIdsInfo(ids) {
  return 'Rush ids: ' + ids.join(', ') + '.';
}

/**
 * @param {RushPreviewContainer[]} containers 
 * @param {number[]} rushIds
 */
function deleteRushContainersFromArray(containers, rushIds) {
  for(const rushId of rushIds) {
    deleteRushContainerFromArray(containers, rushId);
  }
}

function getRushesDeletionCompleteMessage(amount) {
  if(amount === 1) {
    return 'Rush deleted.'
  } else {
    return 'Rushes deleted.'
  }
}
</script>

<style scoped>
.rushes-box {
  padding-top: 1em;
  margin-left: 1em;
  margin-bottom: 1rem;
}
</style>
